import * as Sentry from "@sentry/react";
import { devMode, envName, version } from "@src/env";
import { setupSentry } from "@src/sentry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "react-loading-skeleton/dist/skeleton.css";
import {
  RouterProvider,
  createBrowserRouter as _createBrowserRouter,
} from "react-router-dom";
import { Bounce, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ReactQueryDevtools } from "@src/components/ReactQueryDevtools";

import { setupNativeLinks } from "@src/utils/setupNativeLinks";

import { ErrorBoundary } from "@src/templates/ErrorBoundary";
import { MeuPlay } from "@src/templates/MeuPlay";

import { Autoplay } from "@src/pages/Autoplay";
import { Codecs } from "@src/pages/Codecs";
import { Contact } from "@src/pages/Contact";
import { DeleteAccount } from "@src/pages/DeleteAccount";
import { LiteRedirect } from "@src/pages/LiteRedirect";
import { Login } from "@src/pages/Login";
import { PlayerRenderer } from "@src/pages/PlayerRenderer";
import { Portal } from "@src/pages/Portal";

import "./main.less";

let createBrowserRouter = _createBrowserRouter;
if (devMode) {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="version"]');
  if (meta && meta.content === "%VERSION%") {
    console.log(`Setting version to ${version}`);
    meta.content = version;
  }
} else {
  createBrowserRouter = setupSentry(Sentry);
}

const queryClient = new QueryClient();

// Em wrapper nativo (Electron/RN): <a target="_blank"> interno navega no próprio
// window; externo abre no navegador do SO. No navegador comum é no-op.
setupNativeLinks();

const router = createBrowserRouter([
  {
    path: "/",
    element: <MeuPlay />,
    errorElement: (
      <Sentry.ErrorBoundary fallback={<ErrorBoundary />} showDialog />
    ),
    children: [
      {
        index: true,
        element: <Login />,
      },
      {
        path: "contact",
        element: <Contact />,
      },
      {
        path: "delete-account",
        element: <DeleteAccount />,
      },
      {
        path: "codecs",
        element: <Codecs />,
      },
      {
        path: "portal",
        element: <Portal />,
      },
      {
        path: "autoplay",
        element: <Autoplay />,
      },
      {
        path: "lite/*",
        element: <LiteRedirect />,
      },
      {
        path: ":idRadio",
        element: <PlayerRenderer />,
      },
      {
        path: ":idRadio/:cnpj",
        element: <PlayerRenderer />,
      },
      {
        path: ":idRadio/:cnpj/:senha",
        element: <PlayerRenderer />,
      },
    ],
  },
]);

createRoot(document.getElementById("root")!, {
  // Callback called when an error is thrown and not caught by an Error Boundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an Error Boundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ReactQueryDevtools />
    </QueryClientProvider>
    <ToastContainer
      position="bottom-center"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="dark"
      transition={Bounce}
    />
  </StrictMode>,
);

window.loadedReact = true;

console.log("🌎 envName=", envName);
